import React, { useEffect, useRef, useState } from "react";
import { ColorResult, CustomPicker } from "react-color";
import { InjectedColorProps } from "react-color/lib/components/common/ColorWrap";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";
import {
  CUSTOM_PALETTE_MAX_HELPER,
  DEFAULT_CUSTOM_COLOR,
  DENIM_MAX_CUSTOM_PALETTE_COLORS,
  EMPTY_CUSTOM_SLOT_COLOR,
  MAX_CUSTOM_PALETTE_COLORS,
  collectionBuildBorderClassName,
  helperTextClassName,
  hexInputClassName,
  paletteActionPillClassName,
  paletteSwatchClassName,
} from "./collectionBuildConstants";

const HEX6 = /^#?([0-9a-fA-F]{6})$/;
const HEX3 = /^#?([0-9a-fA-F]{3})$/;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const expandHex3 = (digits: string) =>
  digits
    .split("")
    .map(char => char + char)
    .join("");

const normalizeHex = (value: string): string | null => {
  const trimmed = value.trim();
  const six = trimmed.match(HEX6);
  if (six) {
    return `#${six[1].toUpperCase()}`;
  }
  const three = trimmed.match(HEX3);
  if (three) {
    return `#${expandHex3(three[1]).toUpperCase()}`;
  }
  return null;
};

const isCompleteSixHex = (value: string) => HEX6.test(value.trim());

const pointerRatio = (
  element: HTMLElement | null,
  clientX: number,
  clientY: number
) => {
  const rect = element?.getBoundingClientRect();
  if (!rect) {
    return null;
  }
  return {
    x: clamp((clientX - rect.left) / rect.width, 0, 1),
    y: clamp((clientY - rect.top) / rect.height, 0, 1),
  };
};

const startDrag = (
  event: React.MouseEvent<HTMLDivElement>,
  emit: (clientX: number, clientY: number) => void
) => {
  event.preventDefault();
  emit(event.clientX, event.clientY);
  const handleMove = (moveEvent: MouseEvent) => {
    emit(moveEvent.clientX, moveEvent.clientY);
  };
  const handleUp = () => {
    window.removeEventListener("mousemove", handleMove);
    window.removeEventListener("mouseup", handleUp);
  };
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", handleUp);
};

type HsvColor = { h: number; s: number; v: number };

type SpectrumPickerProps = InjectedColorProps & {
  hsv?: HsvColor;
};

const SpectrumPickerBase: React.FC<SpectrumPickerProps> = ({
  hsl,
  hsv,
  onChange,
}) => {
  const satRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const hue = hsl?.h ?? 0;
  const saturation = hsv?.s ?? 1;
  const value = hsv?.v ?? 1;

  const emitHsv = (next: HsvColor) => {
    if (!onChange) {
      return;
    }
    onChange({
      h: next.h,
      s: next.s,
      v: next.v,
      source: "hsv",
    } as never);
  };

  const handleSaturationDrag = (clientX: number, clientY: number) => {
    const ratio = pointerRatio(satRef.current, clientX, clientY);
    if (!ratio) {
      return;
    }
    emitHsv({
      h: hue,
      s: ratio.x,
      v: 1 - ratio.y,
    });
  };

  const handleHueDrag = (clientX: number, clientY: number) => {
    const ratio = pointerRatio(hueRef.current, clientX, clientY);
    if (!ratio) {
      return;
    }
    emitHsv({
      h: ratio.x * 360,
      s: saturation,
      v: value,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={satRef}
        role="slider"
        aria-label="Saturation and brightness"
        tabIndex={0}
        onMouseDown={event => startDrag(event, handleSaturationDrag)}
        className="relative h-[120px] w-full cursor-crosshair overflow-hidden rounded-lg"
        style={{ backgroundColor: `hsl(${hue}, 100%, 50%)` }}
      >
        <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#fff,transparent)]" />
        <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,transparent,#000)]" />
        <span
          className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.25)]"
          style={{
            left: `${saturation * 100}%`,
            top: `${(1 - value) * 100}%`,
          }}
        />
      </div>
      <div
        ref={hueRef}
        role="slider"
        aria-label="Hue"
        tabIndex={0}
        onMouseDown={event => startDrag(event, handleHueDrag)}
        className="relative h-3.5 w-full cursor-ew-resize"
      >
        <span
          className="pointer-events-none absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 rounded-full"
          style={{
            background:
              "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
          }}
        />
        <span
          className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.25)]"
          style={{ left: `${(hue / 360) * 100}%` }}
        />
      </div>
    </div>
  );
};

const SpectrumPicker = CustomPicker(SpectrumPickerBase);

type Props = {
  onSave: (colors: string[]) => void;
  closeKey: string;
  variant?: "default" | "denim";
};

const CollectionBuildColorPicker: React.FC<Props> = ({
  onSave,
  closeKey,
  variant = "default",
}) => {
  const isDenim = variant === "denim";
  const maxColors = isDenim
    ? DENIM_MAX_CUSTOM_PALETTE_COLORS
    : MAX_CUSTOM_PALETTE_COLORS;
  const emptySlots = (): Array<string | null> =>
    Array.from({ length: maxColors }, () => null);

  const rootRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<Array<string | null>>(emptySlots());
  const onSaveRef = useRef(onSave);
  const isDenimRef = useRef(isDenim);
  const dismissRef = useRef(() => undefined as void);
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState<Array<string | null>>(emptySlots);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const [currentHex, setCurrentHex] = useState(DEFAULT_CUSTOM_COLOR);
  const [hexDraft, setHexDraft] = useState(DEFAULT_CUSTOM_COLOR);
  const [hexError, setHexError] = useState(false);
  const filledColors = slots.filter((hex): hex is string => Boolean(hex));

  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    isDenimRef.current = isDenim;
  }, [isDenim]);

  const resetDraft = () => {
    const next = emptySlots();
    slotsRef.current = next;
    setSlots(next);
    setActiveIndex(0);
    setCurrentHex(DEFAULT_CUSTOM_COLOR);
    setHexDraft(DEFAULT_CUSTOM_COLOR);
    setHexError(false);
  };

  const closePicker = (persist = false) => {
    if (persist) {
      const filled = slotsRef.current.filter((hex): hex is string =>
        Boolean(hex)
      );
      if (filled.length) {
        onSaveRef.current(filled);
      }
    }
    setOpen(false);
    resetDraft();
  };

  const dismissOpenPicker = () => {
    if (isDenimRef.current) {
      closePicker(true);
      return;
    }
    setOpen(false);
  };
  dismissRef.current = dismissOpenPicker;

  const togglePicker = () => {
    if (open) {
      dismissOpenPicker();
      return;
    }
    setOpen(true);
    setActiveIndex(0);
  };

  useEffect(() => {
    setOpen(false);
    resetDraft();
  }, [closeKey, maxColors]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      dismissRef.current();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!hexError) {
      return;
    }
    const timeoutId = window.setTimeout(() => setHexError(false), 2000);
    return () => window.clearTimeout(timeoutId);
  }, [hexError]);

  const applyColorToSlot = (index: number, hex: string) => {
    setSlots(prev => {
      const taken = prev.some(
        (value, slotIndex) =>
          slotIndex !== index && value?.toLowerCase() === hex.toLowerCase()
      );
      if (taken) {
        return prev;
      }
      const next = [...prev];
      next[index] = hex;
      slotsRef.current = next;
      return next;
    });
  };

  const handleColorChange = (hex: string) => {
    const normalized = normalizeHex(hex) ?? hex.toUpperCase();
    setCurrentHex(normalized);
    setHexDraft(normalized);
    setHexError(false);
    if (activeIndex === null) {
      return;
    }
    applyColorToSlot(activeIndex, normalized);
  };

  const commitHexDraft = (value: string) => {
    const normalized = normalizeHex(value);
    if (!normalized) {
      return false;
    }
    handleColorChange(normalized);
    return true;
  };

  const handleHexInputChange = (value: string) => {
    setHexDraft(value);
    setHexError(false);
    if (isCompleteSixHex(value)) {
      commitHexDraft(value);
    }
  };

  const handleHexCommit = () => {
    if (commitHexDraft(hexDraft)) {
      return;
    }
    setHexError(true);
    setHexDraft(currentHex);
  };

  const handleSlotClick = (index: number) => {
    setActiveIndex(index);
    const existing = slots[index];
    if (existing) {
      setCurrentHex(existing);
      setHexDraft(existing);
      setHexError(false);
      return;
    }
    applyColorToSlot(index, currentHex);
  };

  const handleClearSlot = (index: number) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = null;
      slotsRef.current = next;
      return next;
    });
    setActiveIndex(current => (current === index ? null : current));
  };

  const handleSave = () => {
    if (!filledColors.length) {
      return;
    }
    onSave(filledColors);
    closePicker(false);
  };

  return (
    <div ref={rootRef} className="relative shrink-0 self-start">
      <button
        type="button"
        aria-expanded={open}
        onClick={togglePicker}
        className={`${paletteActionPillClassName} gap-2`}
      >
        + Make your own
        {isDenim ? (
          <span
            className={`inline-flex transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          >
            <DropdownArrow className="h-2 w-2" />
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className={`absolute left-0 top-full z-20 mt-2 w-[13rem] rounded-[1.35rem] border ${collectionBuildBorderClassName} bg-white px-3 pb-3 pt-2 shadow-sm`}
        >
          {isDenim ? (
            <div className="mb-2 flex items-center gap-2">
              <span
                className="h-6 w-6 shrink-0 rounded-full border border-black/10"
                style={{ backgroundColor: currentHex }}
              />
              <span className="flex-1 text-xs text-secondary">
                + Make your own
              </span>
              <DropdownArrow className="h-2 w-2 rotate-180" />
            </div>
          ) : null}
          <div>
            <SpectrumPicker
              color={currentHex}
              onChange={(result: ColorResult) => handleColorChange(result.hex)}
            />
          </div>
          <div className="mt-2.5 flex items-center justify-center gap-2">
            {slots.map((hex, index) => {
              const isActive = activeIndex === index;
              return (
                <div key={`slot-${index}`} className="relative">
                  <button
                    type="button"
                    title={hex ? "Edit color" : "Pick a color for this slot"}
                    aria-label={
                      hex
                        ? `Edit color ${hex}`
                        : `Empty color slot ${index + 1}`
                    }
                    onClick={() => handleSlotClick(index)}
                    className={`${paletteSwatchClassName} focus:outline-none ${
                      isActive ? "ring-2 ring-[#737373] ring-offset-1" : ""
                    } ${hex || isDenim ? "" : "border-dashed bg-white"}`}
                    style={{
                      backgroundColor:
                        hex || (isDenim ? EMPTY_CUSTOM_SLOT_COLOR : undefined),
                    }}
                  />
                  {hex && !isDenim ? (
                    <button
                      type="button"
                      title="Clear color"
                      aria-label={`Clear ${hex}`}
                      onClick={() => handleClearSlot(index)}
                      className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-white text-[8px] leading-none text-secondary shadow-sm focus:outline-none"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          {!isDenim ? (
            <>
              <label className="mt-2.5 flex flex-col gap-1">
                <span className="font-avenir text-xs text-secondary">Hex</span>
                <input
                  type="text"
                  value={hexDraft}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  aria-invalid={hexError}
                  aria-label="Hex color"
                  placeholder="#DFD6CD"
                  onChange={event => handleHexInputChange(event.target.value)}
                  onBlur={handleHexCommit}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleHexCommit();
                    }
                  }}
                  className={hexInputClassName}
                />
              </label>
              {hexError ? (
                <p className="mt-1 font-avenir text-[10px] text-negative">
                  Enter a valid hex code
                </p>
              ) : null}
              <p className={`${helperTextClassName} mt-1.5 text-center`}>
                {CUSTOM_PALETTE_MAX_HELPER}
              </p>
              <button
                type="button"
                disabled={!filledColors.length}
                onClick={handleSave}
                className="mt-2 h-8 w-full rounded-full bg-primary px-4 font-avenir text-xs text-inverted focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save palette
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CollectionBuildColorPicker;
